import React, {useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {ACTIVATION_CONSENT_TEXT_VERSION, ActivationView, UpdateActivationTravelerPayload} from '../../domain/entities/Activation.ts'
import {ActivationScope, useActivationMutations} from '../../hooks/useActivations.ts'
import {ConfirmDialog} from '../dialogs/ConfirmDialog.tsx'
import {EditActivationContactDialog} from './EditActivationContactDialog.tsx'
import {ActivationAction} from './activationView.ts'
import {activationErrorMessage} from './activationErrors.ts'

// The traveler as PATCH /activations/{id}/traveler expects it, with overrides.
function travelerPayload(activation: ActivationView, overrides: Partial<UpdateActivationTravelerPayload>): UpdateActivationTravelerPayload {
    const {traveler} = activation
    const consent = traveler.consent
    return {
        firstName: traveler.firstName ?? null,
        lastName: traveler.lastName ?? null,
        email: traveler.email ?? null,
        phone: traveler.phone ?? null,
        preferredChannel: traveler.preferredChannel ?? null,
        noContact: traveler.noContact,
        consent: {
            granted: !!consent?.granted,
            textVersion: consent?.textVersion ?? ACTIVATION_CONSENT_TEXT_VERSION,
            channels: consent?.channels ?? [],
        },
        ...overrides,
    }
}

// Row actions on an activation (pack detail and "No activados" share them):
// returns the dispatcher for ActivationActionsMenu plus the dialogs to mount.
export function useActivationActions(scope: ActivationScope) {
    const {t} = useTranslation('dashboard')
    const {updateActivationTraveler, isUpdatingActivationTraveler, sendActivationCommand, isSendingActivationCommand} = useActivationMutations(scope)
    const editDialog = useRef<React.ElementRef<typeof EditActivationContactDialog>>(null)
    const cancelDialog = useRef<React.ElementRef<typeof ConfirmDialog>>(null)
    const noContactDialog = useRef<React.ElementRef<typeof ConfirmDialog>>(null)
    const [target, setTarget] = useState<ActivationView | null>(null)

    const notifyError = (error: unknown) => enqueueSnackbar(activationErrorMessage(error, t), {variant: 'error'})
    const notifyDone = (key: string) => enqueueSnackbar(t(key), {variant: 'success'})

    const handleAction = (action: ActivationAction, activation: ActivationView) => {
        setTarget(activation)
        switch (action) {
            case 'editContact':
            case 'confirmConsent':
                editDialog.current?.open()
                break
            case 'markNoContact':
                noContactDialog.current?.open()
                break
            case 'cancel':
                cancelDialog.current?.open()
                break
            case 'requestExtra':
                sendActivationCommand({id: activation.id, command: {type: 'REQUEST_EXTRA'}})
                    .then(() => notifyDone('activation.feedback.extraRequested'))
                    .catch(notifyError)
                break
        }
    }

    // ConfirmDialog closes itself when onConfirm returns truthy; the request
    // then runs in the background (same pattern as cancelInvitationCode).
    const confirmCancel = () => {
        if (!target) return false
        sendActivationCommand({id: target.id, command: {type: 'CANCEL'}})
            .then(() => notifyDone('activation.feedback.cancelled'))
            .catch(notifyError)
        return true
    }

    const confirmNoContact = () => {
        if (!target) return false
        updateActivationTraveler({
            id: target.id,
            payload: travelerPayload(target, {
                noContact: true,
                preferredChannel: null,
                consent: {granted: false, textVersion: ACTIVATION_CONSENT_TEXT_VERSION, channels: []},
            }),
        })
            .then(() => notifyDone('activation.feedback.noContactSaved'))
            .catch(notifyError)
        return true
    }

    const saveContact = (payload: UpdateActivationTravelerPayload) => {
        if (!target) return Promise.resolve()
        return updateActivationTraveler({id: target.id, payload})
            .then(() => notifyDone('activation.feedback.contactSaved'))
    }

    const dialogs = (
        <>
            <EditActivationContactDialog ref={editDialog} activation={target} isSaving={isUpdatingActivationTraveler} onSave={saveContact}/>
            <ConfirmDialog
                ref={cancelDialog}
                onConfirm={confirmCancel}
                title={t('activation.cancelConfirm.title')}
                message={t('activation.cancelConfirm.message')}
                confirmLabel={t('activation.action.cancel')}
                cancelLabel={t('cancel')}
            />
            <ConfirmDialog
                ref={noContactDialog}
                onConfirm={confirmNoContact}
                title={t('activation.noContactConfirm.title')}
                message={t('activation.noContactConfirm.message')}
                confirmLabel={t('activation.action.markNoContact')}
                cancelLabel={t('cancel')}
            />
        </>
    )

    return {
        handleAction,
        dialogs,
        isBusy: isUpdatingActivationTraveler || isSendingActivationCommand,
    }
}
